import { Annotation, Column, Group } from '@studio/design-system/form';
import { Mediator } from '@studio/design-system/input';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import { ControlDefaults } from '@studio/models/control';
import { HttpDefaults } from '@studio/models/control/http';
import { toPort } from '@studio/models/net';
import { toAccessor } from '@studio/react-utils';
import { useLatestFn } from '@studio/react-utils/hooks';
import { toString } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { defaults } from 'lodash-es';
import type { ReactElement, SyntheticEvent } from 'react';
import { Slugs } from './constants.js';
import type { HttpProps } from './types.js';
import { Advanced, Credentials, Placeholder } from '#~/common/editor/index.js';
import { Port } from '#~/device/components/port/index.js';

/** `<Http>` settings. */
export const Http = ({ settings, ...props }: HttpProps): ReactElement => {
  const current = defaults({}, settings.value, HttpDefaults, ControlDefaults);

  const handleHostChange = useLatestFn((value: string, event: SyntheticEvent) => {
    const host = toString(value);

    if (host && host !== current.host) {
      settings({ ...current, host });
    } else {
      event.preventDefault();
    }
  });

  const handleCredentialsChange = useLatestFn((credentials: T_Maybe<M_Credentials>) => {
    settings({ ...current, credentials });
  });

  const handlePortChange = useLatestFn((value: number) => {
    const port = toPort(value);

    if (port && port !== current.port) {
      settings({ ...current, port });
    }
  });

  return (
    <Column {...props}>
      <Group annotation={<Annotation.Required />} label='IP address'>
        <Mediator
          onCommit={handleHostChange}
          placeholder={Placeholder.host}
          value={current.host}
          variant={!current.host ? 'error' : undefined}
        />
      </Group>

      <hr className='mt-8' />

      <Advanced slug={Slugs.advanced}>
        <Column>
          <Credentials credentials={toAccessor(current.credentials, handleCredentialsChange)} />

          <Group label='Port'>
            <Port port={toAccessor(current.port, handlePortChange)} />
          </Group>
        </Column>
      </Advanced>
    </Column>
  );
};
